import React, { useState, useEffect } from 'react';
import { CreditCard, Building2, Wallet, Sparkles, CheckCircle, Loader2, AlertCircle, Banknote, Copy, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PaymentInfo({ 
  grandTotal = 0, 
  clientPayAmount,
  showCoproduccion = false,
  coproduccionPercent = 50,
  coproduccionAmount = 0,
  formatPrice, 
  currency, 
  customerEmail, 
  customerName, 
  customerPhone,
  quoteId,
  onSaveQuote,
  termsAccepted = false,
  onValidationError,
  savedQuoteCode = '',
  showBankTransfer = true
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProvider, setLoadingProvider] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  
  // Use clientPayAmount if co-production is active, otherwise grandTotal
  const effectiveTotal = (showCoproduccion && clientPayAmount !== undefined) ? clientPayAmount : grandTotal;
  const depositAmount = effectiveTotal * 0.10;
  const depositFormatted = formatPrice ? formatPrice(depositAmount) : `$${depositAmount.toFixed(2)}`;
  
  const validateBeforePayment = () => {
    const errors = [];
    if (!customerName || customerName.trim() === '') errors.push('El nombre del cliente es obligatorio');
    if (!customerEmail || customerEmail.trim() === '') errors.push('El email del cliente es obligatorio');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) errors.push('El email no tiene un formato válido');
    if (!termsAccepted) errors.push('Debes aceptar los términos y condiciones');
    if (effectiveTotal <= 0) errors.push('Selecciona al menos un servicio');
    return errors;
  };
  
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentSuccessParam = urlParams.get('payment_success');
    const paymentCancelled = urlParams.get('payment_cancelled');
    const provider = urlParams.get('provider');
    
    if (paymentCancelled) {
      toast.error('Pago cancelado');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    if (paymentSuccessParam) {
      if (provider === 'mercadopago') {
        setPaymentSuccess(true);
        toast.success('¡Pago con Mercado Pago exitoso! Gracias por tu anticipo.');
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (sessionId) {
        pollPaymentStatus(sessionId);
      }
    }
  }, []);
  
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error('Tiempo de espera agotado. Verifica tu email.');
      return;
    }
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setPaymentSuccess(true);
        setPaymentStatus(response.data);
        toast.success('¡Pago exitoso! Gracias por tu anticipo.');
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('La sesión de pago expiró.');
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };
  
  const handlePay = async (provider) => {
    const errors = validateBeforePayment();
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      if (onValidationError) onValidationError(errors);
      return;
    }
    
    setIsLoading(true);
    setLoadingProvider(provider);
    
    // Open window BEFORE async call to preserve user gesture context (avoids popup blockers)
    const paymentWindow = window.open('about:blank', '_blank');
    
    try {
      if (provider === 'stripe') {
        const response = await axios.post(`${API}/payments/create-checkout`, {
          amount: parseFloat(depositAmount.toFixed(2)),
          origin_url: window.location.origin,
          quote_id: quoteId || null,
          customer_email: customerEmail || null,
          customer_name: customerName || null,
          description: 'Anticipo 10% - Servicios Audiovisuales Viubux'
        });
        if (response.data.url) {
          if (paymentWindow) {
            paymentWindow.location.href = response.data.url;
          } else {
            window.location.href = response.data.url;
          }
          toast.success('Redirigiendo a Stripe...');
        } else {
          if (paymentWindow) paymentWindow.close();
          toast.error('No se pudo obtener la URL de pago.');
        }
      } else if (provider === 'mercadopago') {
        const response = await axios.post(`${API}/payments/mercadopago/create-preference`, {
          amount: parseFloat(depositAmount.toFixed(2)),
          origin_url: window.location.origin,
          quote_id: quoteId || null,
          customer_email: customerEmail || null,
          customer_name: customerName || null,
          description: 'Anticipo 10% - Servicios Audiovisuales Viubux'
        });
        if (response.data.url) {
          if (paymentWindow) {
            paymentWindow.location.href = response.data.url;
          } else {
            window.location.href = response.data.url;
          }
          toast.success('Redirigiendo a Mercado Pago...');
        } else {
          if (paymentWindow) paymentWindow.close();
          toast.error('No se pudo obtener la URL de pago.');
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      if (paymentWindow) paymentWindow.close();
      toast.error('Error al procesar el pago. Intenta de nuevo.');
    } finally {
      setIsLoading(false);
      setLoadingProvider(null);
    }
  };
  
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      {/* Collapsible header */}
      <button
        onClick={() => setPaymentOpen(prev => !prev)}
        className="w-full flex items-center justify-between p-4 bg-[#0f133a]/80 hover:bg-[#1a1f50]/80 transition-colors"
        data-testid="btn-toggle-payment"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-[#aa2aff] to-[#7012b2]">
            <CreditCard className="w-5 h-5 text-white" />
          </div>
          <span className="font-semibold text-white text-lg">Formas de Pago</span>
        </div>
        {paymentOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>

      {paymentOpen && (
      <div className="p-4 space-y-4">
      {/* Payment Plan Options */}
      {effectiveTotal > 0 && (
        <div className="bg-gradient-to-r from-[#0f133a]/80 to-[#1a1f50]/80 border border-white/10 rounded-xl p-4" data-testid="payment-plan-options">
          <div className="flex items-center gap-2 mb-3">
            <Wallet className="w-4 h-4 text-[#aa2aff]" />
            <p className="text-white font-semibold text-sm">Opciones de Pago</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-white/5 border border-white/10">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Contado (1 exhibición)</p>
              <p className="text-white font-bold text-lg mt-1">
                {formatPrice ? formatPrice(effectiveTotal) : effectiveTotal.toFixed(2)} <span className="text-xs text-slate-400 font-normal">{currency}</span>
              </p>
            </div>
            <div className="p-3 rounded-lg bg-[#aa2aff]/10 border border-[#aa2aff]/30">
              <p className="text-[10px] uppercase tracking-wide text-[#aa2aff]">3 pagos (+16%)</p>
              <p className="text-white font-bold text-lg mt-1">
                3 × {formatPrice ? formatPrice(effectiveTotal * 1.16 / 3) : (effectiveTotal * 1.16 / 3).toFixed(2)}
              </p>
              <p className="text-[10px] text-slate-400 mt-1">
                Total con recargo: {formatPrice ? formatPrice(effectiveTotal * 1.16) : (effectiveTotal * 1.16).toFixed(2)} {currency}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Payment Info Section */}
      <div className="bg-gradient-to-r from-[#0f133a]/80 to-[#1a1f50]/80 border border-white/10 rounded-xl p-5">
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
            <Wallet className="w-5 h-5 text-[#aa2aff] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white font-medium text-sm">Plan de Pagos</p>
              <p className="text-slate-400 text-xs">
                <span className="text-[#aa2aff] font-semibold">1 exhibición</span> o <span className="text-[#aa2aff] font-semibold">3 pagos (+16%)</span>
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
            <Building2 className="w-5 h-5 text-[#aa2aff] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white font-medium text-sm">Métodos Aceptados</p>
              <p className="text-slate-400 text-xs">
                Transferencia, Tarjeta crédito/débito, Mercado Pago, Stripe
              </p>
            </div>
          </div>
          
          {/* Bank Transfer Details - Togglable from admin */}
          {showBankTransfer && (
          <div className="p-3 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-start gap-3">
              <Banknote className="w-5 h-5 text-[#aa2aff] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-white font-medium text-sm mb-2">Transferencia Bancaria</p>
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-xs">Beneficiario:</span>
                    <span className="text-white text-xs font-medium">Carlos Rojas</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-xs">CLABE:</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-white text-xs font-mono font-medium" data-testid="clabe-number">012180026180320848</span>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText('012180026180320848');
                          toast.success('CLABE copiada al portapapeles');
                        }}
                        className="text-[#aa2aff] hover:text-white transition-colors"
                        data-testid="btn-copy-clabe"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {/* Payment Reference */}
                  <div className="flex justify-between items-center pt-1 border-t border-white/5">
                    <span className="text-slate-400 text-xs">Referencia:</span>
                    {savedQuoteCode ? (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[#aa2aff] text-xs font-mono font-bold" data-testid="payment-reference">{savedQuoteCode}</span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(savedQuoteCode);
                            toast.success('Referencia copiada al portapapeles');
                          }}
                          className="text-[#aa2aff] hover:text-white transition-colors"
                          data-testid="btn-copy-reference"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-amber-400/70 text-xs italic">Guarda la cotización para obtener tu referencia</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
      
      {/* Deposit Payment Module */}
      <div className="bg-gradient-to-r from-[#003fdd]/20 to-[#aa2aff]/20 border border-[#aa2aff]/30 rounded-xl p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-gradient-to-br from-[#003fdd] to-[#aa2aff]">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Inicia tu Proyecto Ahora
            </h3>
            <p className="text-slate-400 text-xs">Inicia con tan sólo un 10%</p>
          </div>
        </div>
        
        {paymentSuccess ? (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
            <div>
              <p className="text-green-400 font-semibold">Pago Recibido</p>
              <p className="text-slate-400 text-sm">
                Tu anticipo de {depositFormatted} ha sido procesado exitosamente. 
                Nos pondremos en contacto contigo pronto.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-white/5 rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-slate-400 text-sm">Total:</span>
                <span className={`font-semibold ${showCoproduccion ? 'text-slate-500 line-through text-sm' : 'text-white'}`}>
                  {formatPrice ? formatPrice(grandTotal) : `$${grandTotal.toFixed(2)}`} {currency}
                </span>
              </div>
              {showCoproduccion && coproduccionAmount > 0 && (
                <>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-amber-400 text-sm">Co-Inversionista ({coproduccionPercent}%):</span>
                    <span className="text-amber-400 font-medium">
                      -{formatPrice ? formatPrice(coproduccionAmount) : `$${coproduccionAmount.toFixed(2)}`} {currency}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mb-2 pb-2 border-b border-white/10">
                    <span className="text-white text-sm font-medium">Tu inversión:</span>
                    <span className="text-white font-semibold">
                      {formatPrice ? formatPrice(effectiveTotal) : `$${effectiveTotal.toFixed(2)}`} {currency}
                    </span>
                  </div>
                </>
              )}
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">Anticipo (10%):</span>
                <span className="text-[#aa2aff] font-bold text-xl">
                  {depositFormatted} {currency}
                </span>
              </div>
            </div>
            
            {/* Two payment buttons side by side */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => handlePay('mercadopago')}
                disabled={isLoading || effectiveTotal <= 0}
                className="bg-gradient-to-r from-[#009ee3] to-[#00b1ea] hover:from-[#00b1ea] hover:to-[#009ee3] text-white font-semibold py-5 text-sm shadow-lg"
                data-testid="btn-pay-mercadopago"
              >
                {isLoading && loadingProvider === 'mercadopago' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Wallet className="w-4 h-4 mr-2" />
                )}
                Mercado Pago
              </Button>
              
              <Button
                onClick={() => handlePay('stripe')}
                disabled={isLoading || effectiveTotal <= 0}
                className="bg-gradient-to-r from-[#635bff] to-[#7a73ff] hover:from-[#7a73ff] hover:to-[#635bff] text-white font-semibold py-5 text-sm shadow-lg"
                data-testid="btn-pay-stripe"
              >
                {isLoading && loadingProvider === 'stripe' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Stripe
              </Button>
            </div>
            
            {effectiveTotal <= 0 && (
              <div className="flex items-center gap-2 mt-3 text-amber-400 text-xs">
                <AlertCircle className="w-4 h-4" />
                <span>Selecciona al menos un servicio para habilitar el pago</span>
              </div>
            )}
            
            <p className="text-center text-slate-500 text-xs mt-3">
              Pagos seguros procesados por Stripe y Mercado Pago
            </p>
          </>
        )}
      </div>
      </div>
      )}
    </div>
  );
}

export default PaymentInfo;
